import { ReactElement, useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { ContentLabelField, Loader, TextField } from '@hopskipdrive/react-components';
import * as Yup from 'yup';

import { IncidentTrip } from '../../../types/incidents';
import { StyledRideSlugField } from '../IncidentForm.styles';

interface TripIdFieldProps {
  trip?: IncidentTrip;
  isLoading?: boolean;
  error?: string;
  inputValue: string;
  onInputChange: (newInput: string) => void;
}

export const tripIdValidation = Yup.string().required('Ride slug is required').default('');

export const TripIdField = ({ trip, isLoading, error, inputValue, onInputChange }: TripIdFieldProps): ReactElement => {
  const { control, setError, clearErrors } = useFormContext();
  const { field, fieldState } = useController({
    name: 'tripId',
    control,
    defaultValue: trip?.id,
  });

  useEffect(() => {
    if (trip) {
      field.onChange(trip.id);
    }
  }, [trip]);

  useEffect(() => {
    if (!!error) {
      setError('tripId', {
        message: error,
      });
    } else {
      clearErrors('tripId');
    }
  }, [error]);

  return (
    <ContentLabelField
      id="rideSlug"
      title="Ride slug"
      editMode={true}
      required={true}
      editView={
        <TextField
          InputProps={{
            endAdornment: isLoading && <Loader size={20} />,
          }}
          variant="outlined"
          placeholder="Enter ride slug"
          fullWidth
          value={inputValue}
          onChange={(e) => {
            onInputChange(e.target.value);
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
        />
      }
    />
  );
};

// A version that is static and excluded from the form data

interface TripIdFieldStaticProps {
  defaultValue?: string;
}

export const TripIdFieldStatic = ({ defaultValue }: TripIdFieldStaticProps): ReactElement => {
  return (
    <ContentLabelField
      id="tripId"
      title="Ride slug"
      editMode={true}
      required={true}
      editView={<StyledRideSlugField variant="outlined" fullWidth disabled value={defaultValue} />}
    />
  );
};
